"use client";

import React, { useEffect, useState, forwardRef, ElementRef, ComponentPropsWithoutRef } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { Link as ScrollLink } from 'react-scroll';
import { SignedIn, SignedOut } from '@clerk/nextjs';
import Link from 'next/link';
import Image from 'next/image';
// Import framer-motion and lucide-react icons
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home, // Placeholder, change as needed for "Features"
  Award, // For "Bounties"
  Users, // For "Affiliate" (Could also use Share2)
  Building2, // For "Enterprise"
  BookOpen, // For "Philosophy"
  Rss, // For "Blog"
  HelpCircle, // For "FAQ"
  ChevronDown, // For dropdown arrows
  Icon as LucideIcon,
} from 'lucide-react';
import { ThemeToggle } from "@/components/theme-toggle";
import { useTheme } from 'next-themes';

import { cn } from "@/lib/utils";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  // navigationMenuTriggerStyle, // Not directly used if custom styling trigger
} from "@/components/ui/navigation-menu";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

const Navbar = () => {
  const router = useRouter();
  const pathname = usePathname();
  const [isHomePage, setIsHomePage] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeMobileAccordion, setActiveMobileAccordion] = useState<string | null>(null);

  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setIsHomePage(pathname === '/');
  }, [pathname]);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    const handleScroll = () => {
      // Clear any existing timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      
      // Set a new timeout
      timeoutId = setTimeout(() => {
        const scrollPosition = window.scrollY;
        setIsScrolled(scrollPosition > 5); // Increased threshold slightly
      }, 2); // Small debounce time
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  const handleRedirect = (isSignIn: boolean = false) => {
    router.push(isSignIn ? '/sign-in?' : '/bot-setup');
    setIsMobileMenuOpen(false);
    setActiveMobileAccordion(null);
  };

  const handleNavigation = (sectionId: string) => {
    if (!isHomePage) {
      router.push('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
    setIsMobileMenuOpen(false);
    setActiveMobileAccordion(null);
  };

  const closeMobileMenuAndAccordions = () => {
    setIsMobileMenuOpen(false);
    setActiveMobileAccordion(null);
  };

  // Define types for DropdownMenuItem props
  interface DropdownMenuItemProps {
    href?: string;
    target?: string; // Made optional
    children: React.ReactNode;
    onClick?: () => void;
    isScrollItem?: boolean;
    scrollId?: string;
    icon?: React.ElementType; // Use React.ElementType for icon components
    subheading?: string;
  }

  // Adapted DropdownMenuItem for mobile (used within Collapsible)
  const DropdownMenuItem: React.FC<DropdownMenuItemProps> = ({ 
    href, 
    target, 
    children, 
    onClick, 
    isScrollItem = false, 
    scrollId, 
    icon: IconComponent, 
    subheading 
  }) => {
    const commonClasses = "flex items-start p-4 rounded-lg hover:bg-accent transition-colors duration-150 w-full text-left group";
    const textClasses = "text-muted-foreground group-hover:text-foreground text-sm ml-3 flex flex-col";
    const subheadingClasses = "text-muted-foreground/70 text-xs mt-1";
    const iconContainerClasses = "flex-shrink-0 w-10 h-10 rounded-md bg-accent flex items-center justify-center transition-colors duration-150";
    const iconClasses = "w-5 h-5 text-muted-foreground group-hover:text-foreground";

    const handleClick = () => {
      closeMobileMenuAndAccordions();
      if (onClick) onClick();
    };

    const renderContent = () => (
      <>
        <div className={iconContainerClasses}>
          {IconComponent && <IconComponent className={iconClasses} />}
        </div>
        <div className={textClasses}>
          <span>{children}</span>
          {subheading && <span className={subheadingClasses}>{subheading}</span>}
        </div>
      </>
    );

    if (isScrollItem) {
      if (isHomePage) {
        return (
          <ScrollLink
            to={scrollId}
            smooth={true}
            duration={500}
            onClick={() => { handleNavigation(scrollId); handleClick(); }}
            className={`${commonClasses} cursor-pointer`}
          >
            {renderContent()}
          </ScrollLink>
        );
      } else {
        return (
          <button
            onClick={() => { handleNavigation(scrollId); handleClick(); }}
            className={`${commonClasses}`}
          >
            {renderContent()}
          </button>
        );
      }
    }

    if (href && href.startsWith('http')) {
      return (
        <a href={href} target={target || "_blank"} rel="noopener noreferrer" className={`${commonClasses}`} onClick={handleClick}>
          {renderContent()}
        </a>
      );
    }

    if (href && href.startsWith('mailto:')) {
        return (
          <a href={href} className={`${commonClasses}`} onClick={handleClick}>
             {renderContent()}
          </a>
        );
    }

    if (href) {
        return (
          <Link href={href} className={`${commonClasses}`} onClick={handleClick}>
             {renderContent()}
          </Link>
        );
    }

     return (
        <button onClick={handleClick} className={`${commonClasses}`}>
            {renderContent()}
        </button>
     );
  };

  // ListItem component for Desktop NavigationMenu
  const ListItem = forwardRef<
    ElementRef<typeof Link>,
    ComponentPropsWithoutRef<typeof Link> & {
      title: string;
      icon?: React.ElementType; // Changed LucideIcon to React.ElementType
      subheading?: string;
      isScrollItem?: boolean;
      scrollId?: string;
      // onClick prop from Link will be used, or custom handling for scroll items
    }
  >(({ className, title, icon: IconComponent, subheading, href, isScrollItem, scrollId, ...props }, ref) => {
    
    const content = (
      <>
        {IconComponent && (
          <div className="flex-shrink-0 w-10 h-10 rounded-md bg-accent dark:bg-accent/50 flex items-center justify-center mr-3 transition-colors duration-150 group-hover:bg-primary/10 dark:group-hover:bg-accent">
            <IconComponent className="w-5 h-5 text-muted-foreground group-hover:text-primary dark:group-hover:text-accent-foreground" />
          </div>
        )}
        <div className="flex flex-col">
          <div className="text-sm font-semibold leading-tight text-foreground group-hover:text-primary dark:group-hover:text-accent-foreground">{title}</div>
          {subheading && <p className="text-xs leading-snug text-muted-foreground/80 group-hover:text-muted-foreground">{subheading}</p>}
        </div>
      </>
    );
  
    const itemClasses = cn(
      "flex select-none items-start text-left rounded-md p-3 text-sm no-underline outline-none transition-colors w-full group",
      "hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
      "dark:hover:bg-accent/70",
      className
    );
  
    if (isScrollItem) {
      const scrollAction = () => {
        handleNavigation(scrollId!); // handleNavigation already closes mobile menu if open
        // For desktop, NavigationMenu closes itself on item interaction usually.
      };
      // For ScrollLink, we can't directly use NavigationMenuLink asChild if ScrollLink doesn't support 'as' or ref forwarding well for 'a'
      // So, we render it as a styled button or div-like structure.
      return (
          <button // Using a button for consistent behavior and styling
            onClick={scrollAction}
            className={itemClasses}
          >
            {content}
          </button>
      );
    }
  
    // For actual links (Next.js or external)
    return (
      <NavigationMenuLink asChild>
        <Link
          href={href || "/"} 
          ref={ref}
          className={itemClasses}
          target={typeof href === 'string' && href.startsWith('http') ? "_blank" : undefined}
          rel={typeof href === 'string' && href.startsWith('http') ? "noopener noreferrer" : undefined}
          // onClick for Link is handled by NavigationMenuLink or direct Link behavior
          {...props} 
        >
          {content}
        </Link>
      </NavigationMenuLink>
    );
  });
  ListItem.displayName = "ListItem";

  // Updated navigationItems function
  const navigationItems = (isMobile: boolean) => {
    const navLinkItems = [
      {
        title: "For Creators",
        items: [
          { label: "Features", isScrollItem: true, scrollId: "features", icon: Home, subheading: "Discover our powerful creator tools" },
          { label: "Bounties", href: "/creators", icon: Award, subheading: "Earn rewards for your content" },
          { label: "Affiliate", href: "https://affiliate.saved.gg", icon: Users, subheading: "Join our creator network" },
        ]
      },
      {
        title: "For Brands",
        items: [
          { label: "Enterprise", href: "/enterprise", icon: Building2, subheading: "Run content campaigns" },
        ]
      },
      {
        title: "About",
        items: [
          { label: "Philosophy", href: "/about", icon: BookOpen, subheading: "Learn about our mission" },
          { label: "Blog", href: "https://blog.saved.gg", icon: Rss, subheading: "Latest updates and insights" },
          { label: "FAQ", href: "/faq", icon: HelpCircle, subheading: "Find answers to common questions" },
        ]
      }
    ];

    if (isMobile) {
      return (
        <div className="flex flex-col">
          <Link
            href="/pricing"
            className="text-muted-foreground hover:text-foreground transition-colors duration-200 text-md font-semibold block w-full py-4 px-4 border-b border-border"
            onClick={closeMobileMenuAndAccordions}
          >
            Pricing
          </Link>
          {navLinkItems.map((group) => (
            <Collapsible
              key={group.title}
              open={activeMobileAccordion === group.title}
              onOpenChange={(isOpen) => setActiveMobileAccordion(isOpen ? group.title : null)}
              className="w-full border-b border-border"
            >
              <CollapsibleTrigger className="flex items-center justify-between w-full py-4 px-4 text-md font-semibold text-muted-foreground hover:text-foreground transition-colors duration-200">
                {group.title}
                <ChevronDown
                  className={cn("h-4 w-4 transition-transform duration-200", activeMobileAccordion === group.title && "rotate-180")}
                />
              </CollapsibleTrigger>
              <CollapsibleContent className="overflow-hidden data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up">
                <div className="py-2 pl-6 pr-4 space-y-1 bg-accent/10">
                  {group.items.map((item, index) => (
                    <DropdownMenuItem
                      key={index}
                      href={item.href}
                      isScrollItem={item.isScrollItem}
                      scrollId={item.scrollId}
                      icon={item.icon}
                      subheading={item.subheading}
                      onClick={item.isScrollItem ? () => handleNavigation(item.scrollId!) : undefined}
                    >
                      {item.label}
                    </DropdownMenuItem>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          ))}
        </div>
      );
    }

    // Desktop Navigation
    return (
      <NavigationMenu className="hidden lg:flex items-center space-x-1 whitespace-nowrap">
        <NavigationMenuList className="space-x-1">
          <NavigationMenuItem>
            <Link href="/pricing" legacyBehavior passHref>
              <NavigationMenuLink 
                className="text-muted-foreground hover:text-foreground transition-colors duration-200 text-md font-semibold px-4 py-2 rounded-md hover:bg-accent focus:bg-accent"
                onClick={closeMobileMenuAndAccordions} // Though desktop, this is harmless
              >
                Pricing
              </NavigationMenuLink>
            </Link>
          </NavigationMenuItem>

          {navLinkItems.map((group) => (
            <NavigationMenuItem key={group.title}>
              <NavigationMenuTrigger className="text-muted-foreground hover:text-foreground transition-colors duration-200 text-md font-semibold h-auto py-2">
                {group.title}
              </NavigationMenuTrigger>
              <NavigationMenuContent>
                <ul className="grid gap-3 p-4 md:w-[500px] lg:w-[600px] grid-cols-1 md:grid-cols-2">
                  {group.items.map((item) => (
                    <li key={item.label}>
                      <ListItem
                        title={item.label}
                        href={item.href}
                        icon={item.icon}
                        subheading={item.subheading}
                        isScrollItem={item.isScrollItem}
                        scrollId={item.scrollId}
                      />
                    </li>
                  ))}
                </ul>
              </NavigationMenuContent>
            </NavigationMenuItem>
          ))}
        </NavigationMenuList>
      </NavigationMenu>
    );
  };

  // --- Header and rest of the component ---
  return (
    <header className="bg-transparent sticky top-0 z-50">
      <div className="mx-auto px-7 pt-3">
        <div className={`flex justify-between items-center px-4 h-16 sticky bg-white dark:bg-[#0b0b0c] rounded-xl transition-[max-width,border] duration-300 ease-in-out ${
          isScrolled ? 'border border-border max-w-[75%] mx-auto' : 'max-w-full border-transparent mx-auto'
        }`}>
          {/* Logo Section */}
          <div className="flex items-center flex-shrink-0">
            <Link href="/" onClick={closeMobileMenuAndAccordions}>
              <div className="relative h-8 w-auto cursor-pointer" style={{ width: 152.96, height: 32 }}>
                {mounted ? (
                  <Image
                    key={resolvedTheme}
                    src={resolvedTheme === 'dark' 
                      ? "https://storage.googleapis.com/saved_media_assets/SavedLogo.png"
                      : "https://storage.googleapis.com/saved_media_assets/landing/SavedLogoLight.png"
                    }
                    alt="Saved Logo"
                    width={152.96}
                    height={32}
                    priority
                    className="h-8 w-auto"
                  />
                ) : (
                  <div style={{ width: 152.96, height: 32 }} className="h-8 w-auto" />
                )}
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          {navigationItems(false)}

          {/* Desktop Buttons */}
          <div className="hidden lg:flex items-center space-x-3">
            <ThemeToggle />
            <SignedOut>
              <Link href="/sign-in">
                <button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-2 rounded-md text-md font-semibold transition-colors duration-200">
                  Sign In
                </button>
              </Link>
            </SignedOut>
            <SignedIn>
              <Link href="/bot-setup">
                <button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-2 rounded-md text-md font-semibold transition-colors duration-200">
                  Dashboard
                </button>
              </Link>
            </SignedIn>
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden text-foreground hover:text-foreground/80 focus:outline-none p-2 -mr-2"
            onClick={() => {
              const willBeOpen = !isMobileMenuOpen;
              setIsMobileMenuOpen(willBeOpen);
              if (!willBeOpen) {
                setActiveMobileAccordion(null); // Close accordions when mobile menu closes
              }
            }}
            aria-label="Toggle menu"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              {isMobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile menu panel */}
       <motion.div
        initial={false}
        animate={isMobileMenuOpen ? "open" : "closed"}
        variants={{
          open: { opacity: 1, height: "auto", transition: { duration: 0.3, ease: [0.83, 0, 0.17, 1] } },
          closed: { opacity: 0, height: 0, transition: { duration: 0.3, ease: [0.83, 0, 0.17, 1] } }
        }}
        className={`lg:hidden overflow-hidden bg-transparent`}
      >
        <div className="pb-4 bg-popover dark:bg-[#0b0b0c]/95 backdrop-blur-sm mt-2 mx-4 rounded-lg border border-border shadow-lg"> {/* Added background, blur, margin, rounded, border, shadow */}
          {navigationItems(true)}

          {/* Mobile Auth Buttons & Discord */}
          <div className="pt-4 mt-2 px-4 space-y-3">
             <a
              href="https://discord.gg/MqdzdRr6DP"
              target="_blank"
              rel="noopener noreferrer"
              onClick={closeMobileMenuAndAccordions}
              className="w-full flex justify-center items-center gap-2 text-muted-foreground hover:text-foreground bg-accent hover:bg-accent/90 px-6 py-3 rounded-md text-md font-semibold transition-colors duration-200 mb-2"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/></svg>
              Join Discord
            </a>

            <SignedOut>
              <Link href="/sign-in" onClick={closeMobileMenuAndAccordions}>
                <button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-3 rounded-md text-md font-semibold transition-colors duration-200">
                  Sign In
                </button>
              </Link>
            </SignedOut>
            <SignedIn>
              <Link href="/bot-setup" onClick={closeMobileMenuAndAccordions}>
                <button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-3 rounded-md text-md font-semibold transition-colors duration-200">
                  Dashboard
                </button>
              </Link>
            </SignedIn>
          </div>
        </div>
      </motion.div>
    </header>
  );
};

export default Navbar;